import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import HNLNavLogo from "@/assets/certificates/hnl-logo.png";
import activityIcon from "@/assets/icons/ui/activity-icon.svg";
import { PDFContext } from "./types";
import { ReviewPlanData } from "../../pages/planner/components/review/types";
import { careerPlanPDFTexts } from "./careerPlanPDFTexts";
import { educationDisplayMap } from "../../pages/planner/components/EducationCard";

// Import fonts (reuse from existing certificate PDF)
// @ts-ignore
import ProximaNovaReg from "@/assets/fonts/proximanova_regular.ttf";
// @ts-ignore
import ProximaNovaBold from "@/assets/fonts/proximanova_bold.otf";
// @ts-ignore
import Nunito from "@/assets/fonts/Nunito-Bold.ttf";
// @ts-ignore
import Mulish from "@/assets/fonts/Mulish-Medium.ttf";
// @ts-ignore
import MulishBold from "@/assets/fonts/Mulish-Bold.ttf";

// Register fonts
Font.register({ family: "ProximaNova", src: ProximaNovaReg });
Font.register({
  family: "ProximaNovaBold",
  src: ProximaNovaBold,
  fontWeight: "bold",
});
Font.register({ family: "Nunito", src: Nunito, fontWeight: "bold" });
Font.register({ family: "Mulish", src: Mulish, fontWeight: 400 });
Font.register({ family: "MulishBold", src: MulishBold, fontWeight: 700 });

// Styles based on Figma design
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    fontFamily: "ProximaNova",
    width: 648,
    height: 872,
  },

  // Header with colored progress bar
  header: {
    flexDirection: "column",
    width: "100%",
  },
  progressBar: {
    height: 6,
    flexDirection: "row",
    width: "100%",
  },
  progressSegment: {
    flex: 1,
    height: 6,
  },

  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingTop: 6,
    height: 34,
  },
  logo: {
    width: 89.588,
    height: 18.816,
  },
  studentInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  studentIdText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },

  // Section Header
  sectionHeader: {
    paddingHorizontal: 0,

    alignItems: "center",
    gap: 2,
  },
  sectionLabel: {
    backgroundColor: "#1c252e",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionLabelText: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#ffffff",
    textAlign: "center",
  },
  studentName: {
    fontSize: 32,
    fontFamily: "Nunito",
    fontWeight: "bold",
    color: "#1c252e",
    textAlign: "center",
    width: "100%",
    marginBottom: 8,
  },

  // Main content
  mainContent: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 0,
    gap: 16,
  },

  // Section styles
  section: {
    gap: 2,
    width: "100%",
  },
  sectionTitle: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#637381",
    textTransform: "uppercase",
    marginBottom: 4,
  },

  sectionSubtitle: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#637381",
    marginBottom: 4,
  },

  // Pathway section
  pathwayContent: {
    flexDirection: "row",
    gap: 16,
    alignItems: "flex-start",
  },
  pathwayImage: {
    width: 80,
    height: 80,
    borderRadius: 6,
    backgroundColor: "#f4f6f8",
    objectFit: "cover",
  },
  pathwayTextContent: {
    flex: 1,
    gap: 4,
  },
  pathwayTitle: {
    fontSize: 16,
    marginBottom: 4,
    fontFamily: "ProximaNovaBold",
    color: "#010101",
  },
  pathwayCluster: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#010101",
  },
  pathwayClusterBold: {
    fontFamily: "ProximaNovaBold",
  },
  pathwayDescription: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1C252E",
  },
  reflectionBox: {
    backgroundColor: "#f4f6f8",
    border: "1px dashed #d1d5db",
    borderRadius: 8,
    padding: 4,
    marginTop: 4,
    gap: 2,
  },
  reflectionTitle: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },
  reflectionText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },

  // Hat section
  hatContent: {
    flexDirection: "row",
    gap: 16,
    alignItems: "flex-start",
  },
  hatAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#ffffff",
    objectFit: "cover",
  },
  hatTextContent: {
    flex: 1,
    gap: 4,
  },
  hatTitle: {
    fontSize: 16,
    marginTop: 4,
    marginBottom: 4,
    fontFamily: "ProximaNovaBold",
    color: "#010101",
  },
  hatDescription: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },

  // Divider
  divider: {
    height: 1,
    borderBottom: "1px dashed #d1d5db",
    width: "100%",
    marginVertical: 0,
  },

  // Building blocks
  buildingBlocksGrid: {
    gap: 16,
  },
  buildingBlocksRow: {
    flexDirection: "row",
    gap: 24,
  },
  buildingBlockItem: {
    width: "48%",
    gap: 8,
  },
  buildingBlockHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  buildingBlockIcon: {
    width: 16,
    height: 16,
  },
  buildingBlockTitle: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    flex: 1,
    overflow: "hidden",
  },
  buildingBlockDescription: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },

  // Footer
  footer: {
    backgroundColor: "#ededed",
    height: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 8,
  },
  footerText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },

  // Page 2 specific styles
  twoColumnLayout: {
    flexDirection: "row",
    gap: 16,
    width: "100%",
    marginBottom: 0,
  },
  column: {
    flex: 1,
    marginBottom: -16,
  },
  coreTypeItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  coreTypeAvatar: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: "#ffffff",
  },
  coreTypeName: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    flex: 1,
  },
  coreTypeLevel: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#637381",
    textTransform: "uppercase",
  },
  coreTypeBar: {
    width: "100%",
    height: 6,
    backgroundColor: "#f4f6f8",
    borderRadius: 3,
    marginTop: 6,
  },
  coreTypeProgress: {
    height: 6,
    borderRadius: 3,
  },
  workValueItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  workValueIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: "#ffffff",
  },
  workValueName: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    flex: 1,
  },
  greatForBox: {
    backgroundColor: "#f4f6f8",
    border: "1px dashed #d1d5db",
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
  },
  greatForTitle: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#637381",
    marginBottom: 12,
    textAlign: "center",
  },
  greatForRow: {
    flexDirection: "row",
    gap: 4,
  },
  greatForColumn: {
    flex: 1,
  },
  greatForLabel: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#1C252E",
    marginBottom: 8,
    textTransform: "uppercase",
  },
  greatForItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  greatForItemIcon: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "#ffffff",
  },
  greatForItemText: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },
  educationItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  educationIcon: {
    width: 16,
    height: 16,
  },
  educationText: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },
  courseItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  courseDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    // Color will be set dynamically based on building block name
  },
  courseText: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    flex: 1,
    lineHeight: 1.4,
  },
  nextStepItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 20,
  },
  nextStepNumber: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  nextStepNumberText: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
  },
  nextStepIcon: {
    width: 24,
    height: 24,
    backgroundColor: "#ffffff",
  },
  nextStepContent: {
    flex: 1,
  },
  nextStepTitle: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },
  nextStepDescription: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#637381",
  },
  categoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 20,
  },
  categoryHeaderIcon: {
    width: 16,
    height: 16,
  },
  categoryHeaderText: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },
  notesBox: {
    backgroundColor: "rgba(255, 171, 0, 0.08)",
    border: "1px solid #DFE3E8",
    borderRadius: 8,
    padding: 8,
    height: 100,
  },
  notesTitle: {
    fontSize: 9,
    fontFamily: "ProximaNovaBold",
    color: "#637381",
    marginBottom: 12,
    textTransform: "uppercase",
  },
  notesLines: {
    flexDirection: "column",
    gap: 8,
  },
  notesLine: {
    borderBottom: "1px solid #DFE3E8",
    height: 10,
  },
});

export interface CareerPlanPDFProps extends PDFContext {
  data: ReviewPlanData;
  cmsData?: {
    pathwayCms?: any;
    hatCms?: any;
    coreTypesCms?: any;
    workValuesCms?: any;
    educationCms?: any;
    buildingBlocksCms?: any;
    buildingBlockActivitiesCms?: any;
  };
  activityIconConverted?: string;
}

// Helper function to check if URL is SVG
const isSvgUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url.toLowerCase().includes(".svg") || url.toLowerCase().includes("svg")
  );
};

// Helper to check if a hostname resolves to a private IP range
const isPrivateOrLocalhost = (hostname: string): boolean => {
  // Check for localhost and loopback
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  ) {
    return true;
  }

  // Check for private IP ranges (IPv4)
  const privateIPRanges = [
    /^10\./, // 10.0.0.0/8
    /^172\.(1[6-9]|2\d|3[01])\./, // 172.16.0.0/12
    /^192\.168\./, // 192.168.0.0/16
    /^169\.254\./, // 169.254.0.0/16 (link-local, includes metadata service)
    /^127\./, // 127.0.0.0/8 (loopback)
    /^0\./, // 0.0.0.0/8
  ];

  return privateIPRanges.some((regex) => regex.test(hostname));
};

// Sanitize and validate URL against SSRF attacks
const sanitizeAndValidateUrl = (
  url: string | undefined,
): string | undefined => {
  if (!url) return undefined;

  try {
    const trimmed = url.trim();
    // Use URL constructor for proper validation
    const parsedUrl = new URL(trimmed);

    // Only allow http and https protocols
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      console.warn("Invalid URL protocol - only http/https allowed");
      return undefined;
    }

    // Check for private IPs and localhost
    if (isPrivateOrLocalhost(parsedUrl.hostname)) {
      console.warn("Access to private/internal URLs is not allowed");
      return undefined;
    }

    // Optional: Implement domain allowlist here if needed
    // const allowedDomains = ['trusted-cdn.com', 'images.example.com'];
    // if (!allowedDomains.some(domain => parsedUrl.hostname.endsWith(domain))) {
    //   console.warn('Domain not in allowlist');
    //   return undefined;
    // }

    // Return the sanitized URL
    return parsedUrl.href;
  } catch (error) {
    // Invalid URL format
    console.warn("Invalid URL format");
    return undefined;
  }
};

// Helper function to get non-SVG image URL (skips SVGs)
const getNonSvgImageUrl = (
  ...urls: (string | undefined)[]
): string | undefined => {
  const validUrl = urls.find((url) => url && !isSvgUrl(url));
  return validUrl ? sanitizeAndValidateUrl(validUrl) : undefined;
};

// Helper function to extract text from rich text JSON structure
const extractTextFromRichText = (content: any): string => {
  if (!content) return "";

  // If it's already a string, return it
  if (typeof content === "string") {
    try {
      // Try to parse if it's a JSON string
      const parsed = JSON.parse(content);
      return extractTextFromRichText(parsed);
    } catch {
      // If parsing fails, it's just a plain string
      return content;
    }
  }

  // If it's an array, process each item
  if (Array.isArray(content)) {
    return content.map((item) => extractTextFromRichText(item)).join(" ");
  }

  // If it's an object with children, process the children
  if (content.children && Array.isArray(content.children)) {
    return content.children
      .map((child: any) => extractTextFromRichText(child))
      .join("");
  }

  // If it's an object with text property, return the text
  if (content.text !== undefined) {
    return content.text;
  }

  return "";
};

// Get course icon color based on building block name (matches CourseCard.tsx)
const getCourseIconColor = (buildingBlockName: string): string => {
  const courseColorMap: Record<string, string> = {
    "All Core Academics": "#6366f1", // Indigo
    "Art and Design": "#ec4899", // Pink
    "Art and Technology": "#f472b6", // Pink-400
    Business: "#84cc16", // Lime
    "Computer Science": "#06b6d4", // Cyan
    English: "#8b5cf6", // Purple
    "Family and Consumer Science": "#14b8a6", // Teal
    "Foreign Language": "#f97316", // Orange
    "Health and Physical Education": "#ef4444", // Red
    "Health or CTE": "#dc2626", // Red-600
    "Health or Psychology": "#b91c1c", // Red-700
    Math: "#87ceeb", // Blue
    "Physical Education": "#f87171", // Red-400
    Physics: "#10b981", // Green
    Psychology: "#a855f7", // Violet
    Science: "#663399",
    "Social Studies": "#f59e0b", // Amber
    "Technical or CTE": "#64748b", // Slate
    Technology: "#0891b2", // Cyan-600
  };

  return courseColorMap[buildingBlockName] || "#6b7280"; // Gray fallback
};

// Decode HTML entities (for CMS data that comes with encoded entities)
const decodeHtmlEntities = (str: string): string => {
  if (!str) return "";
  return str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'");
};

// Basic text escape to prevent any HTML-like content from being interpreted downstream
// For PDF context, we decode HTML entities first since CMS data is already encoded
const escapeText = (value: any): string => {
  const str = value == null ? "" : String(value);
  // First decode any HTML entities from CMS, then return for PDF
  // PDFs don't interpret HTML, so we just need the decoded text
  return decodeHtmlEntities(str);
};

// Convenience: extract rich text then escape
const safeRichText = (content: any): string =>
  escapeText(extractTextFromRichText(content));

// Sanitize image URL to safe schemes only (http/https with SSRF protection, blob, data PNG/JPEG, local paths)
const sanitizeImageSrc = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  const trimmed = url.trim();
  const lower = trimmed.toLowerCase();

  // Block dangerous protocols
  if (
    lower.startsWith("javascript:") ||
    lower.startsWith("vbscript:") ||
    lower.startsWith("file:")
  ) {
    console.warn("Dangerous protocol blocked in image URL");
    return undefined;
  }

  // Allow only PNG/JPEG data URLs (already base64 encoded, safe)
  if (lower.startsWith("data:")) {
    if (/^data:image\/(png|jpeg|jpg);/i.test(trimmed)) return trimmed;
    console.warn("Only PNG/JPEG data URLs are allowed");
    return undefined;
  }

  // Allow blob URLs (created by our own code, safe)
  if (lower.startsWith("blob:")) return trimmed;

  // For http/https URLs, validate against SSRF
  if (lower.startsWith("http://") || lower.startsWith("https://")) {
    return sanitizeAndValidateUrl(trimmed);
  }

  // Allow local/relative paths that are PNG/JPG files (webpack module paths)
  // These are safe as they're bundled assets, not user input
  if (/\.(png|jpg|jpeg)(\?|$)/i.test(trimmed)) {
    return trimmed;
  }

  // Log without user input to prevent log injection
  console.warn("Unsupported URL scheme detected");
  return undefined;
};

// Helper function to process placeholders in next step body text
const processStepBody = (body: string, hatDisplayName?: string): string => {
  if (!body || !hatDisplayName) return body;

  // Determine the appropriate article based on the first letter of the hat name
  const firstLetter = hatDisplayName.charAt(0).toLowerCase();
  const vowels = ["a", "e", "i", "o", "u"];
  const article = vowels.includes(firstLetter) ? "an" : "a";
  const hatWithArticle = `${article} ${hatDisplayName}`;

  return body.replace(/\[article chosen Hat\]/g, hatWithArticle);
};

export const CareerPlanPDF: React.FC<CareerPlanPDFProps> = ({
  data,
  config,
  metadata,
  cmsData,
  activityIconConverted,
}) => {
  // Resolve external images from CMS data and fallback to regular data
  const hatImageUrl =
    cmsData?.pathwayCms?.hatImage?.url ||
    data.hat?.displayImage?.sizes?.hatPage?.url ||
    data.topHat?.avatar ||
    undefined;

  const pathwayImageUrl =
    cmsData?.pathwayCms?.image?.url ||
    cmsData?.pathwayCms?.displayImage?.url ||
    data.pathway?.displayImage?.url ||
    undefined;

  // Note: Core type and work value icons are now fetched directly from CMS data
  // in the userCoreTypes and userWorkValues computations below

  const educationImageUrl = (educationId: string) => {
    // Get the PNG icon from educationDisplayMap by education ID
    const educationData =
      educationDisplayMap[educationId as keyof typeof educationDisplayMap];
    return educationData?.iconPNG || undefined;
  };

  // Helper to get building block activity by ID
  const getBuildingBlockActivity = (buildingBlockId: string) => {
    return data.buildingBlocksActivities.find(
      (activity) => String(activity.id) === String(buildingBlockId),
    );
  };

  // Helper to get building block image URL using the same logic as ConnectBuildingBlocksStep
  const buildingBlockImageUrl = (buildingBlockId: string) => {
    // Find the activity by its ID (same as in ConnectBuildingBlocksStep)
    const activity = getBuildingBlockActivity(buildingBlockId);

    if (activity?.details?.category?.icon?.url) {
      return sanitizeImageSrc(activity.details.category.icon.url);
    }
    return undefined;
  };

  // Great For section - get top RIASEC types and work values from hat data
  const hatTopRiasecAndWorkValues = React.useMemo(() => {
    const hatData = cmsData?.hatCms || data.hat;

    if (!hatData) {
      return { topRiasec: [], topWorkValues: [] };
    }

    // Get top 2 RIASEC types from hat's riasecScores
    // Map lowercase riasec names to CMS facetCodes (e.g., "realistic" -> "RIASEC:R")
    const riasecMapping: Record<string, string> = {
      realistic: "RIASEC:R",
      investigative: "RIASEC:I",
      artistic: "RIASEC:A",
      social: "RIASEC:S",
      enterprising: "RIASEC:E",
      conventional: "RIASEC:C",
    };

    const riasecScores = hatData.riasecScores || {};
    const allRiasecEntries = Object.entries(riasecScores)
      .map(([key, value]) => ({
        type: escapeText(key || ""), // Sanitize key from user data
        score: value as number,
        facetCode: escapeText(
          riasecMapping[key.toLowerCase()] ||
            `RIASEC:${key.charAt(0).toUpperCase()}`,
        ), // Sanitize facetCode to prevent XSS
      }))
      .sort((a, b) => b.score - a.score);

    // Filter scores >= 4.5, take top 3
    let riasecEntries = allRiasecEntries
      .filter((item) => item.score >= 4.5)
      .slice(0, 3);

    // If none match threshold, take the top 1 (highest score overall)
    if (riasecEntries.length === 0) {
      riasecEntries = allRiasecEntries.slice(0, 1);
    }

    // Get top 2 work values from hat's workValues
    // Match hat's work value keys to CMS facets using known label mappings
    const workValueLabelMapping: Record<string, string> = {
      achievement: "Accomplishing Goals",
      independence: "Being Independent",
      recognition: "Getting Recognition",
      relationships: "Building Relationships",
      support: "Trust & Support",
      workingConditions: "Stability & Security",
    };

    const workValues = hatData.workValues || {};
    const workValueEntries = Object.entries(workValues)
      .map(([key, value]) => {
        const expectedLabel = workValueLabelMapping[key];

        // Try to find matching CMS facet by:
        // 1. Exact match on longLabel
        // 2. Partial match on longLabel
        // 3. Match on variable name
        const cmsFacet = cmsData?.workValuesCms?.find((f: any) => {
          if (!f) return false;

          const longLabel = (f.longLabel || "").toLowerCase();
          const variable = (f.variable || "").toLowerCase();
          const keyLower = key.toLowerCase();

          // First try exact label match
          if (expectedLabel && longLabel === expectedLabel.toLowerCase()) {
            return true;
          }

          // Then try partial label match
          if (
            expectedLabel &&
            longLabel.includes(expectedLabel.toLowerCase())
          ) {
            return true;
          }

          // Then try variable name match
          if (variable === keyLower || variable.includes(keyLower)) {
            return true;
          }

          // Finally try key includes variable
          if (keyLower.includes(variable) && variable.length > 3) {
            return true;
          }

          return false;
        });

        return {
          type: escapeText(key || ""), // Sanitize key from user data
          score: value as number,
          facetCode: escapeText(
            cmsFacet?.facetCode || `WORK_VALUE:${key.toUpperCase()}`,
          ), // Sanitize facetCode to prevent XSS
          cmsFacet, // Store the found CMS facet for later use
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 2);

    // Find matching CMS facets for icons and labels
    const topRiasec = riasecEntries.map((entry) => {
      const cmsFacet = cmsData?.coreTypesCms?.find(
        (f: any) => f.facetCode === entry.facetCode,
      );
      return {
        facetCode: entry.facetCode, // Already sanitized above
        label: escapeText(cmsFacet?.longLabel || entry.type || ""), // Sanitize label to prevent XSS
        icon:
          sanitizeImageSrc(cmsFacet?.icon) ||
          sanitizeImageSrc(cmsFacet?.image?.url) ||
          sanitizeImageSrc(cmsFacet?.avatar?.url),
      };
    });

    const topWorkValues = workValueEntries.map((entry) => {
      // Use the cmsFacet we already found instead of searching again
      const cmsFacet = entry.cmsFacet;
      return {
        facetCode: entry.facetCode, // Already sanitized above
        label: escapeText(cmsFacet?.longLabel || entry.type || ""), // Sanitize label to prevent XSS
        icon:
          sanitizeImageSrc(cmsFacet?.icon) ||
          sanitizeImageSrc(cmsFacet?.image?.url) ||
          sanitizeImageSrc(cmsFacet?.avatar?.url),
      };
    });

    return { topRiasec, topWorkValues };
  }, [
    cmsData?.hatCms,
    data.hat,
    cmsData?.coreTypesCms,
    cmsData?.workValuesCms,
  ]);

  // Next Steps icons: For education steps without building blocks, use activity icon
  const nextStepImageUrl = (step: any) => {
    // If it has a building block icon, use that
    if (step?.buildingBlock?.icon?.url) {
      return sanitizeImageSrc(step.buildingBlock.icon.url);
    }

    // If it's an education step (has educationOptions but no buildingBlock), use activity icon
    if (step?.category === "education" && step?.educationOptions?.length > 0) {
      return activityIconConverted;
    }

    return undefined;
  };

  // Get user's actual core types (RIASEC facets) from userFacets
  const userCoreTypes = (data.userFacets || [])
    .filter((facet) => facet.facet.startsWith("RIASEC:"))
    .map((facet) => {
      // Find matching CMS facet data
      const cmsFacet = cmsData?.coreTypesCms?.find(
        (cms: any) => cms.facetCode === facet.facet,
      );

      // Calculate percentage score
      const percentage =
        facet.score[1] === 0 ? 0 : (facet.score[0] / facet.score[1]) * 100;

      // Sanitize name from user/CMS input to prevent XSS
      // Sanitize each source value individually before constructing the final name
      const sanitizedLongLabel = cmsFacet?.longLabel
        ? escapeText(cmsFacet.longLabel)
        : null;
      const sanitizedCmsFacet = cmsFacet?.facet
        ? escapeText(cmsFacet.facet)
        : null;
      // Extract and sanitize facet code, removing RIASEC: prefix
      const rawFacetCode = facet.facet || "";
      const sanitizedFacetCode = rawFacetCode
        ? escapeText(rawFacetCode.replace("RIASEC:", ""))
        : "";

      // Use the first available sanitized value
      const sanitizedName =
        sanitizedLongLabel || sanitizedCmsFacet || sanitizedFacetCode || "";

      return {
        name: sanitizedName,
        percentage,
        facetCode: escapeText(facet.facet || ""), // Sanitize facetCode to prevent XSS
        color: cmsFacet?.color || "#6b7280", // Use color from CMS facet data (same as facet-score-box.jsx)
        iconUrl:
          sanitizeImageSrc(cmsFacet?.icon) ||
          sanitizeImageSrc(cmsFacet?.image?.url) ||
          sanitizeImageSrc(cmsFacet?.avatar?.url),
      };
    })
    .sort((a, b) => b.percentage - a.percentage); // Sort by percentage score, highest first

  // Get user's actual work values from userFacets
  // First, get all work value facet codes from CMS (same as TopWorkValuesSection.jsx)
  const workValueFacetCodes = (cmsData?.workValuesCms || [])
    .filter((f: any) => f.facet === "Values")
    .map((f: any) => f.facetCode);

  // Then filter userFacets to only include work values and take top 2
  const userWorkValues = (data.userFacets || [])
    .filter((facet) => workValueFacetCodes.includes(facet.facet))
    .sort((a, b) => {
      // Sort by score descending (score is [current, max]), then alphabetically
      // by facet code so equal scores resolve deterministically — same tiebreak
      // as computeTopFacets, so the PDF agrees with the on-screen profile.
      const aPercent = a.score[1] === 0 ? 0 : (a.score[0] / a.score[1]) * 100;
      const bPercent = b.score[1] === 0 ? 0 : (b.score[0] / b.score[1]) * 100;
      return bPercent !== aPercent
        ? bPercent - aPercent
        : (a.facet || "").localeCompare(b.facet || "");
    })
    .slice(0, 2) // Top 2 work values
    .map((facet) => {
      // Find matching CMS facet data
      const cmsFacet = cmsData?.workValuesCms?.find(
        (cms: any) => cms.facetCode === facet.facet,
      );

      // Sanitize name and facetCode from user/CMS input to prevent XSS
      // Sanitize each source value individually before constructing the final name
      const sanitizedLongLabel = cmsFacet?.longLabel
        ? escapeText(cmsFacet.longLabel)
        : null;
      const sanitizedCmsFacet = cmsFacet?.facet
        ? escapeText(cmsFacet.facet)
        : null;
      const sanitizedFacetCode = facet.facet ? escapeText(facet.facet) : "";

      // Use the first available sanitized value
      const sanitizedName =
        sanitizedLongLabel || sanitizedCmsFacet || sanitizedFacetCode || "";

      return {
        name: sanitizedName,
        facetCode: escapeText(facet.facet || ""), // Sanitize facetCode to prevent XSS
        iconUrl:
          sanitizeImageSrc(cmsFacet?.icon) ||
          sanitizeImageSrc(cmsFacet?.image?.url) ||
          sanitizeImageSrc(cmsFacet?.avatar?.url),
      };
    });

  // Organize building blocks activities by building block
  // The buildingBlockId in careerPlan.buildingBlocks is the activity ID (same as in ConnectBuildingBlocksStep)
  const buildingBlocksWithActivities = data.buildingBlocks.map((block) => {
    // Get the activity by its ID
    const activity = getBuildingBlockActivity(block.buildingBlockId);

    // Get the category name from the activity
    const blockName = activity?.details?.category?.name || "Building Block";

    return {
      block,
      blockName,
      activity, // Single activity, not array
      connectionText: block.connectionText,
    };
  });

  // Group next steps by category and flatten into array with category headers
  const groupAndFlattenNextSteps = (nextSteps: any[]) => {
    const groups: Record<string, { items: any[]; educationId?: string }> = {
      General: { items: [] },
      "Pathway Specific": { items: [] },
    };

    // Define priority order for generic building blocks
    const buildingBlockPriority: Record<string, number> = {
      "Hat Fitting": 1,
      "Work Warm-up": 2,
    };

    // Group the steps
    nextSteps.forEach((step) => {
      if (step.category === "generic") {
        groups["General"].items.push(step);
      } else if (step.category === "pathway") {
        groups["Pathway Specific"].items.push(step);
      } else if (step.category === "education") {
        // Get the education ID from educationOptions
        const educationId = step.educationOptions?.[0]?.educationId;
        if (educationId && educationDisplayMap[educationId]) {
          const categoryName = educationDisplayMap[educationId].title;

          // Create group if it doesn't exist
          if (!groups[categoryName]) {
            groups[categoryName] = { items: [], educationId };
          }

          groups[categoryName].items.push(step);
        }
      }
    });

    // Sort generic items by building block priority
    if (groups["General"].items.length > 0) {
      groups["General"].items.sort((a, b) => {
        const nameA = a.buildingBlock?.name || "";
        const nameB = b.buildingBlock?.name || "";
        const priorityA = buildingBlockPriority[nameA] || 999;
        const priorityB = buildingBlockPriority[nameB] || 999;
        return priorityA - priorityB;
      });
    }

    // Flatten: insert category name before its items
    const flattenedArray: any[] = [];
    Object.entries(groups).forEach(([categoryName, groupData]) => {
      if (groupData.items.length > 0) {
        // Add category header with icon and color if available
        const categoryHeader: any = {
          type: "category-header",
          categoryName: categoryName,
        };

        // Add icon and color from educationDisplayMap if this is an education category
        if (
          groupData.educationId &&
          educationDisplayMap[groupData.educationId]
        ) {
          categoryHeader.icon =
            educationDisplayMap[groupData.educationId].iconPNG;
          categoryHeader.color =
            educationDisplayMap[groupData.educationId].color;
        }

        flattenedArray.push(categoryHeader);
        // Add all items in this category
        flattenedArray.push(...groupData.items);
      }
    });

    return flattenedArray;
  };

  const flattenedNextSteps = groupAndFlattenNextSteps(data.nextSteps || []);
  // Take only first 3 building blocks for page 1
  const page1BuildingBlocks = buildingBlocksWithActivities.slice(0, 3);

  // Determine how many next steps to show on page 2 based on core types and work values
  const hasUserData = userCoreTypes.length > 0 || userWorkValues.length > 0;
  const page2NextStepsCount = hasUserData ? 2 : 6;
  const page3NextStepsCount = 11;
  const additionalPageNextStepsCount = 11;

  // Calculate page splits
  const page2NextSteps = flattenedNextSteps.slice(0, page2NextStepsCount);
  const page3NextSteps = flattenedNextSteps.slice(
    page2NextStepsCount,
    page2NextStepsCount + page3NextStepsCount,
  );
  const remainingNextSteps = flattenedNextSteps.slice(
    page2NextStepsCount + page3NextStepsCount,
  );

  // Calculate total pages
  const additionalPagesNeeded = Math.ceil(
    remainingNextSteps.length / additionalPageNextStepsCount,
  );
  const totalPages = 3 + additionalPagesNeeded;

  return (
    <Document title={config?.title || "Career Plan"}>
      <Page size="A4" style={styles.page}>
        {/* Header with colored progress bar */}
        <View style={styles.header}>
          <View style={[styles.progressBar, { backgroundColor: "#FFD100" }]} />
          <View style={styles.headerContent}>
            <Image
              src={HNLNavLogo}
              style={{ width: 90, height: 18.816, marginBottom: 4 }}
            />
            <View style={styles.studentInfo}>
              <Text style={styles.studentIdText}>
                {careerPlanPDFTexts.header.studentIdLabel}
              </Text>
              <Text style={styles.studentIdText}>
                {escapeText(data.user.studentId)}
              </Text>
            </View>
          </View>
        </View>

        {/* Section Header */}
        <View style={styles.sectionHeader}>
          <View style={styles.sectionLabel}>
            <Text style={styles.sectionLabelText}>
              {careerPlanPDFTexts.header.sectionLabel}
            </Text>
          </View>
          <Text style={styles.studentName}>{escapeText(data.user.name)}</Text>
        </View>

        {/* Main Content */}
        <View style={styles.mainContent}>
          {/* Pathway Possibility Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {careerPlanPDFTexts.sections.pathwayPossibility}
            </Text>
            <Text style={styles.sectionSubtitle}>
              {careerPlanPDFTexts.sections.pathwayPossibilitySubtitle}
            </Text>
            <View style={styles.pathwayContent}>
              {sanitizeImageSrc(pathwayImageUrl) ? (
                <Image
                  src={sanitizeImageSrc(pathwayImageUrl)!}
                  style={styles.pathwayImage}
                />
              ) : (
                <View style={styles.pathwayImage} />
              )}
              <View style={styles.pathwayTextContent}>
                <Text style={styles.pathwayTitle}>
                  {escapeText(data.pathway.name)}
                </Text>
                <Text style={styles.pathwayCluster}>
                  {careerPlanPDFTexts.pathway.careerClusterLabel}{" "}
                  <Text style={styles.pathwayClusterBold}>
                    {escapeText(data.pathway.cluster)}
                  </Text>
                </Text>
                <Text style={styles.pathwayDescription}>
                  {safeRichText(data.pathway.description)}
                </Text>
                <View style={styles.reflectionBox}>
                  <Text style={styles.reflectionTitle}>
                    {careerPlanPDFTexts.pathway.whyThisPathway}
                  </Text>
                  <Text style={styles.reflectionText}>
                    {escapeText(
                      data.interestText ||
                        careerPlanPDFTexts.pathway.defaultReflection,
                    )}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Selected Hat Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {careerPlanPDFTexts.sections.selectedHat}
            </Text>
            <Text style={styles.sectionSubtitle}>
              {careerPlanPDFTexts.sections.selectedHatSubtitle}
            </Text>
            <View style={styles.hatContent}>
              {sanitizeImageSrc(hatImageUrl) ? (
                <Image
                  src={sanitizeImageSrc(hatImageUrl)!}
                  style={styles.hatAvatar}
                />
              ) : (
                <View style={styles.hatAvatar} />
              )}
              <View style={styles.hatTextContent}>
                <Text style={styles.hatTitle}>
                  {escapeText(data.hat.displayName)}
                </Text>
                <Text style={styles.hatDescription}>
                  "
                  {safeRichText(
                    data.hat.msTextOverview || data.topHat.description,
                  )}
                  "
                </Text>
              </View>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Building Blocks Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {careerPlanPDFTexts.sections.buildingBlock}
            </Text>
            <Text style={styles.sectionSubtitle}>
              {careerPlanPDFTexts.sections.buildingBlockSubtitle}
            </Text>
            <View style={styles.buildingBlocksGrid}>
              {/* First row - 2 columns */}
              {page1BuildingBlocks.length > 0 && (
                <View style={styles.buildingBlocksRow}>
                  {page1BuildingBlocks.slice(0, 2).map((item, index) => (
                    <View key={index} style={styles.buildingBlockItem}>
                      <View style={styles.buildingBlockHeader}>
                        {buildingBlockImageUrl(item.block.buildingBlockId) ? (
                          <Image
                            src={buildingBlockImageUrl(
                              item.block.buildingBlockId,
                            )}
                            style={styles.buildingBlockIcon}
                          />
                        ) : (
                          <View style={styles.buildingBlockIcon} />
                        )}
                        <Text style={styles.buildingBlockTitle}>
                          {escapeText(item.blockName)}
                          {item.activity?.details?.name &&
                            ` - ${escapeText(item.activity.details.name)}`}
                        </Text>
                      </View>
                      <Text style={styles.buildingBlockDescription}>
                        {escapeText(
                          item.activity?.userActivityDescription ||
                            careerPlanPDFTexts.reflection.defaultDescription,
                        )}
                      </Text>
                      <View style={styles.reflectionBox}>
                        <Text style={styles.reflectionTitle}>
                          {careerPlanPDFTexts.reflection.myReflection}
                        </Text>
                        <Text style={styles.reflectionText}>
                          {escapeText(
                            item.connectionText ||
                              careerPlanPDFTexts.reflection.defaultReason,
                          )}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {/* Second row - 1 column */}
              {page1BuildingBlocks.length > 2 && (
                <View style={styles.buildingBlocksRow}>
                  <View style={styles.buildingBlockItem}>
                    <View style={styles.buildingBlockHeader}>
                      {buildingBlockImageUrl(
                        page1BuildingBlocks[2].block.buildingBlockId,
                      ) ? (
                        <Image
                          src={buildingBlockImageUrl(
                            page1BuildingBlocks[2].block.buildingBlockId,
                          )}
                          style={styles.buildingBlockIcon}
                        />
                      ) : (
                        <View style={styles.buildingBlockIcon} />
                      )}
                      <Text style={styles.buildingBlockTitle}>
                        {escapeText(page1BuildingBlocks[2].blockName)}
                        {page1BuildingBlocks[2].activity?.details?.name &&
                          ` - ${escapeText(page1BuildingBlocks[2].activity.details.name)}`}
                      </Text>
                    </View>
                    <Text style={styles.buildingBlockDescription}>
                      {escapeText(
                        page1BuildingBlocks[2].activity
                          ?.userActivityDescription ||
                          careerPlanPDFTexts.reflection.defaultDescription,
                      )}
                    </Text>
                    <View style={styles.reflectionBox}>
                      <Text style={styles.reflectionTitle}>
                        {careerPlanPDFTexts.reflection.myReflection}
                      </Text>
                      <Text style={styles.reflectionText}>
                        {escapeText(
                          page1BuildingBlocks[2].connectionText ||
                            careerPlanPDFTexts.reflection.defaultReason,
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {careerPlanPDFTexts.footer.documentTitle}
          </Text>
          <Text style={styles.footerText}>Page 1 of {totalPages}</Text>
        </View>
      </Page>

      {/* PAGE 2 */}
      <Page size="A4" style={styles.page}>
        {/* Header with 5-segment progress bar */}
        <View style={styles.header}>
          <View style={[styles.progressBar, { backgroundColor: "#FFD100" }]} />
          <View style={styles.headerContent}>
            <Image
              src={HNLNavLogo}
              style={{ width: 90, height: 18.816, marginBottom: 4 }}
            />
            <View style={styles.studentInfo}>
              <Text style={styles.studentIdText}>
                {careerPlanPDFTexts.header.studentIdLabel}
              </Text>
              <Text style={styles.studentIdText}>
                {escapeText(data.user.studentId)}
              </Text>
            </View>
          </View>
        </View>

        {/* Section Header */}
        <View style={styles.sectionHeader}>
          <View style={styles.sectionLabel}>
            <Text style={styles.sectionLabelText}>
              {careerPlanPDFTexts.header.sectionLabel}
            </Text>
          </View>
          <Text style={styles.studentName}>{escapeText(data.user.name)}</Text>
        </View>

        {/* Main Content */}
        <View style={styles.mainContent}>
          {/* Core Types and Work Values Section */}
          <View style={styles.twoColumnLayout}>
            {/* Left Column - Core Types */}
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>
                {careerPlanPDFTexts.sections.myCoreTypes}
              </Text>
              <Text style={styles.sectionSubtitle}>
                {careerPlanPDFTexts.sections.myCoreTypesSubtitle}
              </Text>
              <View style={{ marginTop: 8 }}>
                {/* User's actual core types */}
                {userCoreTypes.length === 0 ? (
                  <Text
                    style={[
                      styles.coreTypeName,
                      {
                        marginBottom: 24,
                        fontFamily: "ProximaNovaBold",
                        textAlign: "center",
                      },
                    ]}
                  >
                    Not Complete
                  </Text>
                ) : (
                  userCoreTypes.map((coreType, index) => (
                    <View key={index} style={{ marginBottom: 12 }}>
                      <View style={styles.coreTypeItem}>
                        {coreType.iconUrl ? (
                          <Image
                            src={coreType.iconUrl}
                            style={styles.coreTypeAvatar}
                          />
                        ) : (
                          <View style={styles.coreTypeAvatar} />
                        )}
                        <View style={{ flex: 1 }}>
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Text style={styles.coreTypeName}>
                              {coreType.name}
                            </Text>
                            {coreType.percentage >= 60 && (
                              <Text style={styles.coreTypeLevel}>
                                {careerPlanPDFTexts.coreTypes.highInterest}
                              </Text>
                            )}
                          </View>
                          <View style={styles.coreTypeBar}>
                            <View
                              style={[
                                styles.coreTypeProgress,
                                {
                                  width: `${coreType.percentage}%`,
                                  backgroundColor: coreType.color, // Use color from CMS facet data (same as facet-score-box.jsx)
                                },
                              ]}
                            />
                          </View>
                        </View>
                      </View>
                    </View>
                  ))
                )}
              </View>
            </View>

            {/* Right Column - Work Values */}
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>
                {careerPlanPDFTexts.sections.myWorkValues}
              </Text>
              <Text style={styles.sectionSubtitle}>
                {careerPlanPDFTexts.sections.myWorkValuesSubtitle}
              </Text>
              <View style={{ marginTop: 8 }}>
                {/* User's actual work values */}
                {userWorkValues.length === 0 ? (
                  <Text
                    style={[
                      styles.workValueName,
                      {
                        marginBottom: 24,
                        fontFamily: "ProximaNovaBold",
                        textAlign: "center",
                      },
                    ]}
                  >
                    Not Complete
                  </Text>
                ) : (
                  userWorkValues.map((workValue, index) => (
                    <View key={index} style={styles.workValueItem}>
                      {workValue.iconUrl ? (
                        <Image
                          src={workValue.iconUrl}
                          style={styles.workValueIcon}
                        />
                      ) : (
                        <View style={styles.workValueIcon} />
                      )}
                      <Text style={styles.workValueName}>{workValue.name}</Text>
                    </View>
                  ))
                )}

                {/* Great For Box */}
                {userWorkValues.length > 0 &&
                  hatTopRiasecAndWorkValues.topRiasec.length > 0 && (
                    <View style={styles.greatForBox}>
                      <Text style={styles.greatForTitle}>
                        {
                          careerPlanPDFTexts.coreTypes
                            .coreTypesAndWorkValuesOfHat
                        }
                      </Text>
                      <View style={styles.greatForRow}>
                        <View style={styles.greatForColumn}>
                          <Text style={styles.greatForLabel}>
                            {careerPlanPDFTexts.coreTypes.greatForLabel}
                          </Text>
                          {hatTopRiasecAndWorkValues.topRiasec.map(
                            (riasec, index) => (
                              <View key={index} style={styles.greatForItem}>
                                {riasec.icon ? (
                                  <Image
                                    src={riasec.icon}
                                    style={styles.greatForItemIcon}
                                  />
                                ) : (
                                  <View style={styles.greatForItemIcon} />
                                )}
                                <Text style={styles.greatForItemText}>
                                  {riasec.label}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                        <View style={styles.greatForColumn}>
                          <Text style={styles.greatForLabel}>
                            {careerPlanPDFTexts.coreTypes.workValuesLabel}
                          </Text>
                          {hatTopRiasecAndWorkValues.topWorkValues.map(
                            (workValue, index) => (
                              <View key={index} style={styles.greatForItem}>
                                {workValue.icon ? (
                                  <Image
                                    src={workValue.icon}
                                    style={styles.greatForItemIcon}
                                  />
                                ) : (
                                  <View style={styles.greatForItemIcon} />
                                )}
                                <Text style={styles.greatForItemText}>
                                  {workValue.label}
                                </Text>
                              </View>
                            ),
                          )}
                        </View>
                      </View>
                    </View>
                  )}
              </View>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Education and Course Connection */}
          <View style={styles.twoColumnLayout}>
            {/* Left Column - Education & Training */}
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>
                {careerPlanPDFTexts.sections.educationTraining}
              </Text>
              <Text style={styles.sectionSubtitle}>
                {careerPlanPDFTexts.sections.educationTrainingSubtitle}
              </Text>
              <View style={{ marginTop: 8 }}>
                {(() => {
                  // Use educationPathIds if available, otherwise fall back to educationOptions
                  const selectedEducationOptions: Array<{
                    id: string;
                    title: string;
                    iconPNG?: string;
                  }> =
                    (data.educationPathIds || []).length > 0
                      ? ((data.educationPathIds || [])
                          .map((id) => {
                            const eduData =
                              educationDisplayMap[
                                id as keyof typeof educationDisplayMap
                              ];
                            return eduData
                              ? {
                                  id,
                                  title: eduData.title,
                                  iconPNG: eduData.iconPNG,
                                }
                              : null;
                          })
                          .filter((edu) => edu !== null) as Array<{
                          id: string;
                          title: string;
                          iconPNG?: string;
                        }>)
                      : (data.educationOptions || []).map((edu) => ({
                          id: edu.id,
                          title: edu.title,
                          iconPNG: undefined,
                        }));

                  return selectedEducationOptions
                    .slice(0, 4)
                    .map((edu, index) => (
                      <View key={index} style={styles.educationItem}>
                        {sanitizeImageSrc(
                          edu.iconPNG || educationImageUrl(edu.id),
                        ) ? (
                          <Image
                            src={
                              sanitizeImageSrc(
                                edu.iconPNG || educationImageUrl(edu.id),
                              )!
                            }
                            style={styles.educationIcon}
                          />
                        ) : (
                          <View style={styles.educationIcon} />
                        )}
                        <Text style={styles.educationText}>
                          {safeRichText(edu.title)}
                        </Text>
                      </View>
                    ));
                })()}
              </View>
            </View>

            {/* Right Column - Course Connection */}
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>
                {careerPlanPDFTexts.sections.courseConnection}
              </Text>
              <Text style={styles.sectionSubtitle}>
                {careerPlanPDFTexts.sections.courseConnectionSubtitle}
              </Text>
              <View style={{ marginTop: 8 }}>
                {(data.recommendedCourses || [])
                  .slice(0, 5)
                  .map((course, index) => {
                    const buildingBlockName = course.buildingBlock?.name || "";
                    const dotColor = getCourseIconColor(buildingBlockName);
                    return (
                      <View key={index} style={styles.courseItem}>
                        <View
                          style={[
                            styles.courseDot,
                            { backgroundColor: dotColor },
                          ]}
                        />
                        <Text style={styles.courseText}>
                          {escapeText(
                            buildingBlockName ||
                              extractTextFromRichText(course.body),
                          )}
                        </Text>
                      </View>
                    );
                  })}
              </View>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Next Steps Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {careerPlanPDFTexts.sections.nextSteps}
            </Text>
            <Text style={styles.sectionSubtitle}>
              {careerPlanPDFTexts.sections.nextStepsSubtitle}
            </Text>
            <View style={{ marginTop: 8 }}>
              {page2NextSteps.map((item, index) => {
                // Render category header
                if (item.type === "category-header") {
                  return (
                    <View key={`header-${index}`} style={styles.categoryHeader}>
                      {sanitizeImageSrc(item.icon) && (
                        <Image
                          src={sanitizeImageSrc(item.icon)!}
                          style={styles.categoryHeaderIcon}
                        />
                      )}
                      <Text
                        style={[
                          styles.categoryHeaderText,
                          item.color && { color: item.color },
                        ]}
                      >
                        {escapeText(item.categoryName)}
                      </Text>
                    </View>
                  );
                }

                // Render next step item - count within current category across all pages
                const actualIndex = index;
                const lastHeaderIndex = flattenedNextSteps
                  .slice(0, actualIndex + 1)
                  .reverse()
                  .findIndex((i) => i.type === "category-header");

                const categoryStartIndex =
                  lastHeaderIndex === -1
                    ? 0
                    : actualIndex - lastHeaderIndex + 1;

                const stepNumber = flattenedNextSteps
                  .slice(categoryStartIndex, actualIndex + 1)
                  .filter((i) => i.type !== "category-header").length;

                return (
                  <View key={index} style={styles.nextStepItem}>
                    <View style={styles.nextStepNumber}>
                      <Text style={styles.nextStepNumberText}>
                        {stepNumber}
                      </Text>
                    </View>
                    {sanitizeImageSrc(nextStepImageUrl(item)) ? (
                      <Image
                        src={sanitizeImageSrc(nextStepImageUrl(item))!}
                        style={styles.nextStepIcon}
                      />
                    ) : (
                      <View style={styles.nextStepIcon} />
                    )}
                    <View style={styles.nextStepContent}>
                      <Text style={styles.nextStepTitle}>
                        {item.category === "education"
                          ? "Education Next Step"
                          : safeRichText(item.buildingBlock?.name) ||
                            careerPlanPDFTexts.nextSteps.defaultTitle}
                      </Text>
                      <Text style={styles.nextStepDescription}>
                        {escapeText(
                          processStepBody(
                            extractTextFromRichText(item.body),
                            data.hatDisplayName,
                          ),
                        )}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          {/* Notes Section */}
          <View style={styles.section}>
            <View style={styles.notesBox}>
              <Text style={styles.notesTitle}>
                {careerPlanPDFTexts.sections.notes}
              </Text>
              <View style={styles.notesLines}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <View key={i} style={styles.notesLine} />
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {careerPlanPDFTexts.footer.documentTitle}
          </Text>
          <Text style={styles.footerText}>Page 2 of {totalPages}</Text>
        </View>
      </Page>

      {/* PAGE 3 */}
      <Page size="A4" style={styles.page}>
        {/* Header with 5-segment progress bar */}
        <View style={styles.header}>
          <View style={[styles.progressBar, { backgroundColor: "#FFD100" }]} />
          <View style={styles.headerContent}>
            <Text style={styles.studentIdText}>
              {careerPlanPDFTexts.header.logo}
            </Text>
            <View style={styles.studentInfo}>
              <Text style={styles.studentIdText}>
                {careerPlanPDFTexts.header.studentIdLabel}
              </Text>
              <Text style={styles.studentIdText}>
                {escapeText(data.user.studentId)}
              </Text>
            </View>
          </View>
        </View>

        {/* Section Header */}
        <View style={styles.sectionHeader}>
          <View style={styles.sectionLabel}>
            <Text style={styles.sectionLabelText}>
              {careerPlanPDFTexts.header.sectionLabel}
            </Text>
          </View>
          <Text style={styles.studentName}>{escapeText(data.user.name)}</Text>
        </View>

        {/* Main Content */}
        <View style={styles.mainContent}>
          {/* Next Steps Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {careerPlanPDFTexts.sections.nextSteps}
            </Text>
            <Text style={styles.sectionSubtitle}>
              {careerPlanPDFTexts.sections.nextStepsSubtitle}
            </Text>

            <View style={{ marginTop: 8 }}>
              {page3NextSteps.map((item, index) => {
                const actualIndex = index + page2NextStepsCount;

                // Render category header
                if (item.type === "category-header") {
                  return (
                    <View
                      key={`header-${actualIndex}`}
                      style={styles.categoryHeader}
                    >
                      {sanitizeImageSrc(item.icon) && (
                        <Image
                          src={sanitizeImageSrc(item.icon)!}
                          style={styles.categoryHeaderIcon}
                        />
                      )}
                      <Text
                        style={[
                          styles.categoryHeaderText,
                          item.color && { color: item.color },
                        ]}
                      >
                        {escapeText(item.categoryName)}
                      </Text>
                    </View>
                  );
                }

                // Render next step item - count within current category across all pages
                const lastHeaderIndex = flattenedNextSteps
                  .slice(0, actualIndex + 1)
                  .reverse()
                  .findIndex((i) => i.type === "category-header");

                const categoryStartIndex =
                  lastHeaderIndex === -1
                    ? 0
                    : actualIndex - lastHeaderIndex + 1;

                const stepNumber = flattenedNextSteps
                  .slice(categoryStartIndex, actualIndex + 1)
                  .filter((i) => i.type !== "category-header").length;

                return (
                  <View key={actualIndex} style={styles.nextStepItem}>
                    <View style={styles.nextStepNumber}>
                      <Text style={styles.nextStepNumberText}>
                        {stepNumber}
                      </Text>
                    </View>
                    {sanitizeImageSrc(nextStepImageUrl(item)) ? (
                      <Image
                        src={sanitizeImageSrc(nextStepImageUrl(item))!}
                        style={styles.nextStepIcon}
                      />
                    ) : (
                      <View style={styles.nextStepIcon} />
                    )}
                    <View style={styles.nextStepContent}>
                      <Text style={styles.nextStepTitle}>
                        {item.category === "education"
                          ? "Education Next Step"
                          : safeRichText(item.buildingBlock?.name) ||
                            careerPlanPDFTexts.nextSteps.defaultTitle}
                      </Text>
                      <Text style={styles.nextStepDescription}>
                        {escapeText(
                          processStepBody(
                            extractTextFromRichText(item.body),
                            data.hatDisplayName,
                          ),
                        )}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          {/* Spacer to push notes to bottom */}
          <View style={{ flex: 1 }} />

          {/* Notes Section */}
          <View style={[styles.section, { marginBottom: 16 }]}>
            <View style={styles.notesBox}>
              <Text style={styles.notesTitle}>
                {careerPlanPDFTexts.sections.notes}
              </Text>
              <View style={styles.notesLines}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <View key={i} style={styles.notesLine} />
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {careerPlanPDFTexts.footer.documentTitle}
          </Text>
          <Text style={styles.footerText}>Page 3 of {totalPages}</Text>
        </View>
      </Page>

      {/* ADDITIONAL PAGES */}
      {Array.from({ length: additionalPagesNeeded }).map((_, pageIndex) => {
        const startIndex =
          page2NextStepsCount +
          page3NextStepsCount +
          pageIndex * additionalPageNextStepsCount;
        const endIndex = startIndex + additionalPageNextStepsCount;
        const pageNextSteps = flattenedNextSteps.slice(startIndex, endIndex);
        const pageNumber = 4 + pageIndex;

        return (
          <Page
            key={`additional-page-${pageIndex}`}
            size="A4"
            style={styles.page}
          >
            {/* Header with progress bar */}
            <View style={styles.header}>
              <View
                style={[styles.progressBar, { backgroundColor: "#FFD100" }]}
              />
              <View style={styles.headerContent}>
                <Image
                  src={HNLNavLogo}
                  style={{ width: 90, height: 18.816, marginBottom: 4 }}
                />
                <View style={styles.studentInfo}>
                  <Text style={styles.studentIdText}>
                    {careerPlanPDFTexts.header.studentIdLabel}
                  </Text>
                  <Text style={styles.studentIdText}>
                    {escapeText(data.user.studentId)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Section Header */}
            <View style={styles.sectionHeader}>
              <View style={styles.sectionLabel}>
                <Text style={styles.sectionLabelText}>
                  {careerPlanPDFTexts.header.sectionLabel}
                </Text>
              </View>
              <Text style={styles.studentName}>
                {escapeText(data.user.name)}
              </Text>
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
              {/* Next Steps Section */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {careerPlanPDFTexts.sections.nextSteps}
                </Text>
                <Text style={styles.sectionSubtitle}>
                  {careerPlanPDFTexts.sections.nextStepsSubtitle}
                </Text>

                <View style={{ marginTop: 8 }}>
                  {pageNextSteps.map((item, index) => {
                    const actualIndex = startIndex + index;

                    // Render category header
                    if (item.type === "category-header") {
                      return (
                        <View
                          key={`header-${actualIndex}`}
                          style={styles.categoryHeader}
                        >
                          {sanitizeImageSrc(item.icon) && (
                            <Image
                              src={sanitizeImageSrc(item.icon)!}
                              style={styles.categoryHeaderIcon}
                            />
                          )}
                          <Text
                            style={[
                              styles.categoryHeaderText,
                              item.color && { color: item.color },
                            ]}
                          >
                            {escapeText(item.categoryName)}
                          </Text>
                        </View>
                      );
                    }

                    // Render next step item - count within current category across all pages
                    const lastHeaderIndex = flattenedNextSteps
                      .slice(0, actualIndex + 1)
                      .reverse()
                      .findIndex((i) => i.type === "category-header");

                    const categoryStartIndex =
                      lastHeaderIndex === -1
                        ? 0
                        : actualIndex - lastHeaderIndex + 1;

                    const stepNumber = flattenedNextSteps
                      .slice(categoryStartIndex, actualIndex + 1)
                      .filter((i) => i.type !== "category-header").length;

                    return (
                      <View key={actualIndex} style={styles.nextStepItem}>
                        <View style={styles.nextStepNumber}>
                          <Text style={styles.nextStepNumberText}>
                            {stepNumber}
                          </Text>
                        </View>
                        {sanitizeImageSrc(nextStepImageUrl(item)) ? (
                          <Image
                            src={sanitizeImageSrc(nextStepImageUrl(item))!}
                            style={styles.nextStepIcon}
                          />
                        ) : (
                          <View style={styles.nextStepIcon} />
                        )}
                        <View style={styles.nextStepContent}>
                          <Text style={styles.nextStepTitle}>
                            {item.category === "education"
                              ? "Education Next Step"
                              : safeRichText(item.buildingBlock?.name) ||
                                careerPlanPDFTexts.nextSteps.defaultTitle}
                          </Text>
                          <Text style={styles.nextStepDescription}>
                            {escapeText(
                              processStepBody(
                                extractTextFromRichText(item.body),
                                data.hatDisplayName,
                              ),
                            )}
                          </Text>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>

              {/* Spacer to push notes to bottom */}
              <View style={{ flex: 1 }} />

              {/* Notes Section */}
              <View style={[styles.section, { marginBottom: 16 }]}>
                <View style={styles.notesBox}>
                  <Text style={styles.notesTitle}>
                    {careerPlanPDFTexts.sections.notes}
                  </Text>
                  <View style={styles.notesLines}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <View key={i} style={styles.notesLine} />
                    ))}
                  </View>
                </View>
              </View>
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Text style={styles.footerText}>
                {careerPlanPDFTexts.footer.documentTitle}
              </Text>
              <Text style={styles.footerText}>
                Page {pageNumber} of {totalPages}
              </Text>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};
